import React, {
    useState,
    useEffect,
    useImperativeHandle,
    forwardRef,
    Fragment
} from "react";
import { useDropzone } from "react-dropzone";
import PropTypes from "prop-types";
import uuid from "uuid";
import { Translate } from "react-localize-redux";

import DropzoneImagesHorizontal from "../image/DropzoneImagesHorizontal";

const Dropzone = forwardRef(
    (
        {
            placeholder,
            onDrop,
            onRemoveSource,
            multiple,
            defaultSources,
            disableRemove,
            limitFile,
            limitSize,
            description
        },
        ref
    ) => {
        const [dropzoneData, setDropzoneData] = useState({
            files: [],
            sources: []
        });

        useImperativeHandle(ref, () => ({
            resetFiles() {
                setDropzoneData({ ...dropzoneData, files: [] });
            }
        }));

        const { files } = dropzoneData;
        const sources = dropzoneData.sources.map(source => {
            return {
                source
            };
        });

        useEffect(() => {
            if (defaultSources) {
                setDropzoneData({ files, sources: defaultSources });
            }
        }, [defaultSources, files]);

        const { getRootProps, getInputProps } = useDropzone({
            accept: "image/jpeg, image/png",
            multiple,
            onDrop: acceptedFiles => {
                let fileNumbers = 0;

                const formattedAcceptedFiles = acceptedFiles
                    .filter(file => {
                        // Filter Size
                        if (
                            !limitSize ||
                            (limitSize &&
                                (file.size / 1024 / 1024).toFixed(4) <=
                                limitSize)
                        ) {
                            fileNumbers++;
                        } else {
                            alert(`${file.name} is larger than ${limitSize}MB`);
                            return false;
                        }
                        // Filter File number
                        if (
                            !limitFile ||
                            (limitFile &&
                                files.length + fileNumbers <= limitFile)
                        ) {
                            return true;
                        } else {
                            alert(
                                `Only ${limitFile} images can be uploaded, ${file.name} is ignored`
                            );
                            return false;
                        }
                    })
                    .map(file =>
                        Object.assign(file, {
                            preview: URL.createObjectURL(file),
                            id: uuid.v4()
                        })
                    );

                if (formattedAcceptedFiles.length > 0) {
                    const newFiles = multiple
                        ? files.concat(formattedAcceptedFiles)
                        : formattedAcceptedFiles;

                    setDropzoneData({ ...dropzoneData, files: newFiles });

                    onDrop(newFiles);
                }
            }
        });

        const removeSource = removedSource => {
            const remainSources = sources.filter(
                source => source !== removedSource
            );

            setDropzoneData({ ...dropzoneData, sources: remainSources });

            onRemoveSource(removedSource);
        };

        const removeFile = removedFile => {
            const remainFiles = files.filter(
                file => file.preview !== removedFile
            );

            setDropzoneData({ ...dropzoneData, files: remainFiles });

            onDrop(remainFiles);
        };

        return (
            <Translate>
                {({ translate }) => (
                    <Fragment>
                        <div className="dropzone-images">
                            <aside>
                                <DropzoneImagesHorizontal
                                    images={files.map(file => {
                                        return {
                                            source: file.preview
                                        };
                                    })}
                                    removeFile={removeFile}
                                    type="file"
                                />
                            </aside>
                            {(multiple || (!multiple && files.length === 0)) && (
                                <aside>
                                    <DropzoneImagesHorizontal
                                        images={sources}
                                        removeSource={removeSource}
                                        type="source"
                                    />
                                </aside>
                            )}
                        </div>
                        <div {...getRootProps({ className: "dropzone" })}>
                            <p>{placeholder}</p>
                            <input {...getInputProps()} />
                        </div>
                        {description && (
                            <small className="form-text">{description}</small>
                        )}
                        {limitSize && (
                            <small className="form-text">
                                {translate("imageSizeLimitNotice", {
                                    limit: limitSize
                                })}
                            </small>
                        )}
                        {limitFile && (
                            <small className="form-text">
                                {translate("imageFileLimitNotice", {
                                    limit: limitFile
                                })}
                            </small>
                        )}

                    </Fragment>
                )}
            </Translate>
        );
    }
);

Dropzone.propTypes = {
    placeholder: PropTypes.string,
    onDrop: PropTypes.func.isRequired,
    onRemoveSource: PropTypes.func,
    multiple: PropTypes.bool,
    defaultSources: PropTypes.array,
    disableRemove: PropTypes.bool,
    limitFile: PropTypes.number,
    limitSize: PropTypes.number,
    description: PropTypes.string
};

export default Dropzone;
